/**
 * Self-test suite: Disappearing messages (per-thread timer)
 *
 * Mirrors DisappearingMessagesSelfTest.swift exactly. Cases grouped by
 * component so a failure points at the broken layer:
 *
 *   Duration  (1-5)   — value type semantics
 *   Payload   (6-11)  — wire format encode/decode
 *   Store     (12-15) — per-channel timer persistence
 *   Scheduler (16-21) — track / tick / cancel / persistence
 *   Coord     (22-25) — coordinator end-to-end
 *
 * Every case that touches IndexedDB uses a fresh DB name
 * (`kaether-selftest-<uuid>`) and cleans up after itself.
 *
 * Time-dependent cases drive the deterministic `tick(now)` API and never
 * touch the production setInterval timer.
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";
import { DisappearingMessageDuration } from "./DisappearingMessageDuration";
import { DisappearingMessageWireFormat } from "./DisappearingMessagePayload";
import { DisappearingMessageStore } from "./DisappearingMessageStore";
import {
  MessageExpiryScheduler,
  type MessageWiper,
  type ExpiryEntry,
} from "./MessageExpiryScheduler";
import { DisappearingMessages } from "./DisappearingMessages";

// ---------------------------------------------------------------------------
// Fixtures
// ---------------------------------------------------------------------------

/** Drop an IndexedDB database by name. Resolves regardless of existence. */
function dropDb(name: string): Promise<void> {
  return new Promise((resolve) => {
    const req = indexedDB.deleteDatabase(name);
    req.onsuccess = () => resolve();
    req.onerror = () => resolve();
    req.onblocked = () => resolve();
  });
}

/** Fresh unique DB names — one per test so isolation is guaranteed. */
function freshStoreDb(): string {
  return `kaether-selftest-${crypto.randomUUID()}`;
}

function freshSchedulerDb(): string {
  return `kaether-selftest-${crypto.randomUUID()}`;
}

/** A wiper that records calls without side effects — mirrors RecordingWiper. */
class RecordingWiper implements MessageWiper {
  readonly calls: Array<{
    messageId: string;
    channelId: string;
    encryptedContentHash: string;
  }> = [];

  wipe(messageId: string, channelId: string, encryptedContentHash: string): void {
    this.calls.push({ messageId, channelId, encryptedContentHash });
  }
}

// ---------------------------------------------------------------------------
// Duration tests (1–5)
// ---------------------------------------------------------------------------

function testDurationOffSemantics(): TestCase {
  const off = DisappearingMessageDuration.off;
  const ok = off.isOff && off.seconds === 0 && off.ms === 0;
  return ok
    ? pass("duration .off has zero seconds, isOff=true")
    : fail(
        "duration .off has zero seconds, isOff=true",
        `seconds=${off.seconds} isOff=${off.isOff}`,
      );
}

function testDurationFromSeconds(): TestCase {
  const zero = DisappearingMessageDuration.fromSeconds(0).isOff;
  const neg = DisappearingMessageDuration.fromSeconds(-42).isOff;
  const pos = DisappearingMessageDuration.fromSeconds(60).seconds === 60;
  const ok = zero && neg && pos;
  return ok
    ? pass("fromSeconds() clamps non-positive to .off")
    : fail("fromSeconds() clamps non-positive to .off", `zero=${zero} neg=${neg} pos=${pos}`);
}

function testDurationCodableRoundTrip(): TestCase {
  const inputs = [
    DisappearingMessageDuration.off,
    DisappearingMessageDuration.fromSeconds(5),
    DisappearingMessageDuration.fromSeconds(86_400),
  ];
  for (const d of inputs) {
    const raw = d.toJSON();
    const back = DisappearingMessageDuration.fromJSON(raw);
    if (!back.equals(d)) {
      return fail(
        "duration encodes as bare Int and round-trips",
        `failed for seconds=${d.seconds}, got back seconds=${back.seconds}`,
      );
    }
  }
  return pass("duration encodes as bare Int and round-trips");
}

function testDurationDisplayName(): TestCase {
  const cases: [DisappearingMessageDuration, string][] = [
    [DisappearingMessageDuration.off, "Off"],
    [DisappearingMessageDuration.fromSeconds(30), "30s"],
    [DisappearingMessageDuration.fromSeconds(60 * 5), "5m"],
    [DisappearingMessageDuration.fromSeconds(60 * 60), "1h"],
    [DisappearingMessageDuration.fromSeconds(60 * 60 * 24), "1d"],
    [DisappearingMessageDuration.fromSeconds(60 * 60 * 24 * 7), "1w"],
    [DisappearingMessageDuration.fromSeconds(60 * 60 * 24 * 7 * 4), "4w"],
  ];
  for (const [d, expected] of cases) {
    if (d.displayName !== expected) {
      return fail(
        "duration displayName for each ladder rung",
        `${d.seconds}s displayed as '${d.displayName}', want '${expected}'`,
      );
    }
  }
  return pass("duration displayName for each ladder rung");
}

function testDurationPresetsContainOff(): TestCase {
  const presets = DisappearingMessageDuration.presets;
  const firstIsOff = presets[0]?.isOff === true;
  const minCount = presets.length >= 5;
  const secondsSet = new Set(presets.map((p) => p.seconds));
  const noDuplicates = secondsSet.size === presets.length;
  const ok = firstIsOff && minCount && noDuplicates;
  return ok
    ? pass("presets start with .off and are unique")
    : fail("presets start with .off and are unique", `firstIsOff=${firstIsOff} count=${presets.length} noDuplicates=${noDuplicates}`);
}

// ---------------------------------------------------------------------------
// Payload tests (6–11)
// ---------------------------------------------------------------------------

function testPayloadOffPassesThroughVerbatim(): TestCase {
  const wrapped = DisappearingMessageWireFormat.wrap(
    "hi",
    DisappearingMessageDuration.off,
  );
  const ok = wrapped === "hi";
  return ok
    ? pass(".off wrap is verbatim (backwards-compat)")
    : fail(".off wrap is verbatim (backwards-compat)", `got '${wrapped}'`);
}

function testPayloadWrapUnwrapRoundTrip(): TestCase {
  const sentAt = new Date(1_700_000_000 * 1000);
  const wrapped = DisappearingMessageWireFormat.wrap(
    "hello",
    DisappearingMessageDuration.fromSeconds(300),
    sentAt,
  );
  const u = DisappearingMessageWireFormat.unwrap(wrapped);
  const ok =
    u.text === "hello" &&
    u.payload?.ttlSeconds === 300 &&
    u.payload?.sentAt === sentAt.getTime() &&
    wrapped.startsWith(DisappearingMessageWireFormat.prefix);
  return ok
    ? pass("payload wrap → unwrap round-trip preserves text+ttl+sentAt")
    : fail(
        "payload wrap → unwrap round-trip preserves text+ttl+sentAt",
        `text=${u.text} ttl=${u.payload?.ttlSeconds} sentAt=${u.payload?.sentAt}`,
      );
}

function testPayloadUnwrapPlainTextHasNoPayload(): TestCase {
  const u = DisappearingMessageWireFormat.unwrap("just a normal message");
  const ok = u.text === "just a normal message" && u.payload === null && !u.hasTimer;
  return ok
    ? pass("unwrap of plain text yields payload=null")
    : fail("unwrap of plain text yields payload=null", `text=${u.text} hasTimer=${u.hasTimer}`);
}

function testPayloadUnwrapMalformedFallsBack(): TestCase {
  const bad = DisappearingMessageWireFormat.prefix + "{not valid json}";
  const u = DisappearingMessageWireFormat.unwrap(bad);
  // Defensive degrade: prefix-with-bad-payload returns the raw string as text
  // and no payload, rather than crashing or returning null.
  const ok = u.payload === null && u.text === bad;
  return ok
    ? pass("malformed payload degrades to legacy text")
    : fail(
        "malformed payload degrades to legacy text",
        `payload=${u.payload !== null ? "non-null" : "null"} text=${u.text.slice(0, 40)}`,
      );
}

function testPayloadExpiresAtCalculation(): TestCase {
  const sentAtMs = 1_000_000 * 1000;
  const sentAt = new Date(sentAtMs);
  const wrapped = DisappearingMessageWireFormat.wrap(
    "x",
    DisappearingMessageDuration.fromSeconds(60),
    sentAt,
  );
  const u = DisappearingMessageWireFormat.unwrap(wrapped);
  const expectedExpiresMs = sentAtMs + 60 * 1000;
  const ok = u.payload !== null && u.payload.sentAt + u.payload.ttlSeconds * 1000 === expectedExpiresMs;

  // Also test ttl=0 → no expiry
  const wrappedOff = DisappearingMessageWireFormat.wrap(
    "x",
    DisappearingMessageDuration.off,
    sentAt,
  );
  const uOff = DisappearingMessageWireFormat.unwrap(wrappedOff);
  const okOff = uOff.payload === null; // off pass-through → no payload

  return ok && okOff
    ? pass("expiresAt = sentAt + ttl, null when ttl=0")
    : fail("expiresAt = sentAt + ttl, null when ttl=0", `ok=${ok} okOff=${okOff}`);
}

function testPayloadTtlIsEncryptedSafe(): TestCase {
  const wrapped = DisappearingMessageWireFormat.wrap(
    "hello\nworld",
    DisappearingMessageDuration.fromSeconds(60),
  );
  const ok =
    wrapped.length > 0 &&
    !wrapped.startsWith("kaether:e2ee:") && // never collides with outer envelope
    wrapped.startsWith(DisappearingMessageWireFormat.prefix);
  // Round-trip preserves embedded newlines
  const back = DisappearingMessageWireFormat.unwrap(wrapped).text;
  const ok2 = back === "hello\nworld";
  return ok && ok2
    ? pass(
        "wrapped payload survives multi-line text and never collides with outer envelope prefix",
      )
    : fail(
        "wrapped payload survives multi-line text and never collides with outer envelope prefix",
        `ok=${ok} ok2=${ok2} wrapped=${wrapped.slice(0, 40)}`,
      );
}

// ---------------------------------------------------------------------------
// Store tests (12–15)
// ---------------------------------------------------------------------------

async function testStoreDefaultIsOff(): Promise<TestCase> {
  const dbName = freshStoreDb();
  try {
    const store = new DisappearingMessageStore(dbName);
    const timer = await store.getTimer("anything");
    const ok = timer.isOff;
    return ok
      ? pass("store returns .off for unknown channel")
      : fail("store returns .off for unknown channel", `got seconds=${timer.seconds}`);
  } finally {
    await dropDb(dbName);
  }
}

async function testStoreSetGet(): Promise<TestCase> {
  const dbName = freshStoreDb();
  try {
    const store = new DisappearingMessageStore(dbName);
    await store.setTimer(
      "ch1",
      DisappearingMessageDuration.fromSeconds(60),
      "alice",
    );
    const timer = await store.getTimer("ch1");
    const record = await store.record("ch1");
    const ok =
      timer.seconds === 60 && record?.updatedByUserId === "alice";
    return ok
      ? pass("store setTimer/getTimer round-trip + author")
      : fail(
          "store setTimer/getTimer round-trip + author",
          `timer=${timer.seconds}s updatedBy=${record?.updatedByUserId ?? "null"}`,
        );
  } finally {
    await dropDb(dbName);
  }
}

async function testStorePersistsAcrossInstances(): Promise<TestCase> {
  const dbName = freshStoreDb();
  try {
    const writer = new DisappearingMessageStore(dbName);
    await writer.setTimer("ch1", DisappearingMessageDuration.fromSeconds(300));
    const reader = new DisappearingMessageStore(dbName);
    const timer = await reader.getTimer("ch1");
    const ok = timer.seconds === 300;
    return ok
      ? pass("store survives a fresh instance")
      : fail("store survives a fresh instance", `reader saw seconds=${timer.seconds}`);
  } finally {
    await dropDb(dbName);
  }
}

async function testStoreResetAll(): Promise<TestCase> {
  const dbName = freshStoreDb();
  try {
    const store = new DisappearingMessageStore(dbName);
    await store.setTimer("ch1", DisappearingMessageDuration.fromSeconds(60));
    await store.setTimer("ch2", DisappearingMessageDuration.fromSeconds(120));
    await store.resetAll();
    const t1 = await store.getTimer("ch1");
    const t2 = await store.getTimer("ch2");
    const all = await store.allRecords();
    const ok = t1.isOff && t2.isOff && all.length === 0;
    return ok
      ? pass("store resetAll wipes every channel")
      : fail(
          "store resetAll wipes every channel",
          `ch1=${t1.seconds}s ch2=${t2.seconds}s remaining=${all.length}`,
        );
  } finally {
    await dropDb(dbName);
  }
}

// ---------------------------------------------------------------------------
// Scheduler tests (16–21)
// ---------------------------------------------------------------------------

async function testSchedulerTrackThenTickWipes(): Promise<TestCase> {
  const dbName = freshSchedulerDb();
  const wiper = new RecordingWiper();
  const scheduler = new MessageExpiryScheduler(dbName, wiper);
  try {
    const now = new Date();
    await scheduler.track("m1", "c1", "ct1", new Date(now.getTime() + 60_000), now);
    // tick before deadline → no-op
    await scheduler.tick(new Date(now.getTime() + 30_000));
    const beforeOk =
      wiper.calls.length === 0 &&
      (await scheduler.pendingEntries()).length === 1;
    // tick after deadline → wipe + drain
    await scheduler.tick(new Date(now.getTime() + 120_000));
    const afterOk =
      wiper.calls.length === 1 &&
      wiper.calls[0]?.messageId === "m1" &&
      (await scheduler.pendingEntries()).length === 0;
    const ok = beforeOk && afterOk;
    return ok
      ? pass("scheduler fires wipe at the configured deadline")
      : fail(
          "scheduler fires wipe at the configured deadline",
          `before=${beforeOk} after=${afterOk} calls=${wiper.calls.length}`,
        );
  } finally {
    await dropDb(dbName);
  }
}

async function testSchedulerNothingDueDoesNothing(): Promise<TestCase> {
  const dbName = freshSchedulerDb();
  const wiper = new RecordingWiper();
  const scheduler = new MessageExpiryScheduler(dbName, wiper);
  try {
    const drained = await scheduler.tick(new Date());
    const ok = drained.length === 0 && wiper.calls.length === 0;
    return ok
      ? pass("scheduler with no entries is a no-op")
      : fail("scheduler with no entries is a no-op", `drained=${drained.length} calls=${wiper.calls.length}`);
  } finally {
    await dropDb(dbName);
  }
}

async function testSchedulerCancelPreventsWipe(): Promise<TestCase> {
  const dbName = freshSchedulerDb();
  const wiper = new RecordingWiper();
  const scheduler = new MessageExpiryScheduler(dbName, wiper);
  try {
    const now = new Date();
    await scheduler.track("m1", "c1", "ct1", new Date(now.getTime() + 60_000), now);
    await scheduler.cancel("m1");
    await scheduler.tick(new Date(now.getTime() + 120_000));
    const ok =
      wiper.calls.length === 0 &&
      (await scheduler.pendingEntries()).length === 0;
    return ok
      ? pass("cancel(messageId) prevents the wipe")
      : fail(
          "cancel(messageId) prevents the wipe",
          `calls=${wiper.calls.length} pending=${(await scheduler.pendingEntries()).length}`,
        );
  } finally {
    await dropDb(dbName);
  }
}

async function testSchedulerAlreadyExpiredFiresImmediately(): Promise<TestCase> {
  const dbName = freshSchedulerDb();
  const wiper = new RecordingWiper();
  const scheduler = new MessageExpiryScheduler(dbName, wiper);
  try {
    const now = new Date();
    // expiresAt is 60s in the past
    await scheduler.track(
      "m1",
      "c1",
      "ct1",
      new Date(now.getTime() - 60_000),
      now,
    );
    const ok =
      wiper.calls.length === 1 &&
      (await scheduler.pendingEntries()).length === 0;
    return ok
      ? pass("track() with past deadline wipes immediately (catch-up after suspend)")
      : fail(
          "track() with past deadline wipes immediately (catch-up after suspend)",
          `calls=${wiper.calls.length} pending=${(await scheduler.pendingEntries()).length}`,
        );
  } finally {
    await dropDb(dbName);
  }
}

async function testSchedulerPersistsAcrossInstances(): Promise<TestCase> {
  const dbName = freshSchedulerDb();
  const wiper1 = new RecordingWiper();
  const s1 = new MessageExpiryScheduler(dbName, wiper1);
  const now = new Date();
  try {
    await s1.track("m1", "c1", "ct1", new Date(now.getTime() + 60_000), now);

    const wiper2 = new RecordingWiper();
    const s2 = new MessageExpiryScheduler(dbName, wiper2);
    const pendingAfterRelaunch = (await s2.pendingEntries()).length === 1;
    await s2.tick(new Date(now.getTime() + 120_000));
    const firedAfterRelaunch =
      wiper2.calls.length === 1 && wiper1.calls.length === 0;
    const ok = pendingAfterRelaunch && firedAfterRelaunch;
    return ok
      ? pass("scheduler persists across instances; new instance fires the wipe")
      : fail(
          "scheduler persists across instances; new instance fires the wipe",
          `pending=${pendingAfterRelaunch} fired=${firedAfterRelaunch}`,
        );
  } finally {
    await dropDb(dbName);
  }
}

async function testSchedulerReTrackUpdatesDeadline(): Promise<TestCase> {
  const dbName = freshSchedulerDb();
  const wiper = new RecordingWiper();
  const scheduler = new MessageExpiryScheduler(dbName, wiper);
  try {
    const now = new Date();
    await scheduler.track("m1", "c1", "ct1", new Date(now.getTime() + 60_000), now);
    await scheduler.track("m1", "c1", "ct1", new Date(now.getTime() + 600_000), now);
    // After 120s — under the second deadline — entry should still be pending.
    await scheduler.tick(new Date(now.getTime() + 120_000));
    const ok =
      (await scheduler.pendingEntries()).length === 1 && wiper.calls.length === 0;
    return ok
      ? pass("re-track of same messageId replaces the deadline (no duplicate)")
      : fail(
          "re-track of same messageId replaces the deadline (no duplicate)",
          `pending=${(await scheduler.pendingEntries()).length} calls=${wiper.calls.length}`,
        );
  } finally {
    await dropDb(dbName);
  }
}

// ---------------------------------------------------------------------------
// Coordinator tests (22–25)
// ---------------------------------------------------------------------------

async function testCoordWrapForSendUsesChannelTimer(): Promise<TestCase> {
  const storeDb = freshStoreDb();
  const schedDb = freshSchedulerDb();
  try {
    const timerStore = new DisappearingMessageStore(storeDb);
    const scheduler = new MessageExpiryScheduler(schedDb, new RecordingWiper());
    const coord = new DisappearingMessages(timerStore, scheduler);

    const plainBefore = await coord.wrapForSend("hi", "ch1");
    const plainOk = plainBefore === "hi";

    await timerStore.setTimer("ch1", DisappearingMessageDuration.fromSeconds(60));
    const wrapped = await coord.wrapForSend("hi", "ch1");
    const wrappedOk = wrapped.startsWith(DisappearingMessageWireFormat.prefix);

    const ok = plainOk && wrappedOk;
    return ok
      ? pass("coord wrapForSend honours the per-channel timer")
      : fail(
          "coord wrapForSend honours the per-channel timer",
          `plain=${plainOk} wrapped=${wrappedOk}`,
        );
  } finally {
    await dropDb(storeDb);
    await dropDb(schedDb);
  }
}

async function testCoordRemoteTimerLastWriteWins(): Promise<TestCase> {
  const storeDb = freshStoreDb();
  const schedDb = freshSchedulerDb();
  try {
    const timerStore = new DisappearingMessageStore(storeDb);
    const scheduler = new MessageExpiryScheduler(schedDb, new RecordingWiper());
    const coord = new DisappearingMessages(timerStore, scheduler);

    const earlier = new Date(1000 * 1000);
    const later = new Date(2000 * 1000);

    await coord.applyRemoteTimer(
      "ch1",
      DisappearingMessageDuration.fromSeconds(60),
      "alice",
      later,
    );
    await coord.applyRemoteTimer(
      "ch1",
      DisappearingMessageDuration.fromSeconds(30),
      "bob",
      earlier,
    );
    // The earlier update must NOT clobber the later one.
    const timer = await coord.currentTimer("ch1");
    const ok = timer.seconds === 60;
    return ok
      ? pass("applyRemoteTimer: last-write-wins on timestamp")
      : fail(
          "applyRemoteTimer: last-write-wins on timestamp",
          `got seconds=${timer.seconds}`,
        );
  } finally {
    await dropDb(storeDb);
    await dropDb(schedDb);
  }
}

async function testCoordTrackOutgoingNoOpWhenOff(): Promise<TestCase> {
  const storeDb = freshStoreDb();
  const schedDb = freshSchedulerDb();
  try {
    const timerStore = new DisappearingMessageStore(storeDb);
    const scheduler = new MessageExpiryScheduler(schedDb, new RecordingWiper());
    const coord = new DisappearingMessages(timerStore, scheduler);
    await coord.trackOutgoing("m1", "ch1", "ct");
    const pending = (await scheduler.pendingEntries()).length;
    const ok = pending === 0;
    return ok
      ? pass("trackOutgoing is a no-op when timer is .off")
      : fail("trackOutgoing is a no-op when timer is .off", `pending=${pending}`);
  } finally {
    await dropDb(storeDb);
    await dropDb(schedDb);
  }
}

async function testCoordUnwrapReceivedComputesExpiry(): Promise<TestCase> {
  const storeDb = freshStoreDb();
  const schedDb = freshSchedulerDb();
  try {
    const timerStore = new DisappearingMessageStore(storeDb);
    const scheduler = new MessageExpiryScheduler(schedDb, new RecordingWiper());
    const coord = new DisappearingMessages(timerStore, scheduler);
    const sentAt = new Date(1_700_000_000 * 1000);
    const wrapped = DisappearingMessageWireFormat.wrap(
      "hi",
      DisappearingMessageDuration.fromSeconds(60),
      sentAt,
    );
    const { text, expiresAt } = coord.unwrapReceived(wrapped);
    const expectedExpiry = new Date(sentAt.getTime() + 60 * 1000);
    const ok = text === "hi" && expiresAt?.getTime() === expectedExpiry.getTime();
    return ok
      ? pass("unwrapReceived returns text + expiresAt for TTL message")
      : fail(
          "unwrapReceived returns text + expiresAt for TTL message",
          `text=${text} expiresAt=${expiresAt?.toISOString() ?? "null"}`,
        );
  } finally {
    await dropDb(storeDb);
    await dropDb(schedDb);
  }
}

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "Disappearing messages (per-thread timer)",
  isVerified: true,
  run: async () =>
    buildSuiteResult([
      // Duration (1-5)
      testDurationOffSemantics(),
      testDurationFromSeconds(),
      testDurationCodableRoundTrip(),
      testDurationDisplayName(),
      testDurationPresetsContainOff(),
      // Payload (6-11)
      testPayloadOffPassesThroughVerbatim(),
      testPayloadWrapUnwrapRoundTrip(),
      testPayloadUnwrapPlainTextHasNoPayload(),
      testPayloadUnwrapMalformedFallsBack(),
      testPayloadExpiresAtCalculation(),
      testPayloadTtlIsEncryptedSafe(),
      // Store (12-15)
      await testStoreDefaultIsOff(),
      await testStoreSetGet(),
      await testStorePersistsAcrossInstances(),
      await testStoreResetAll(),
      // Scheduler (16-21)
      await testSchedulerTrackThenTickWipes(),
      await testSchedulerNothingDueDoesNothing(),
      await testSchedulerCancelPreventsWipe(),
      await testSchedulerAlreadyExpiredFiresImmediately(),
      await testSchedulerPersistsAcrossInstances(),
      await testSchedulerReTrackUpdatesDeadline(),
      // Coordinator (22-25)
      await testCoordWrapForSendUsesChannelTimer(),
      await testCoordRemoteTimerLastWriteWins(),
      await testCoordTrackOutgoingNoOpWhenOff(),
      await testCoordUnwrapReceivedComputesExpiry(),
    ] as TestCase[]),
});
